import { attributes } from '../../core/ids-attributes';

export type TimeParts = {
  hours: number,
  minutes: number,
  seconds: number,
  period: string
};

export const IdsTimePickerCommonAttributes = [
  attributes.AUTOSELECT,
  attributes.AUTOUPDATE,
  attributes.DISABLED,
  attributes.EMBEDDABLE,
  attributes.ID,
  attributes.LABEL,
  attributes.NO_MARGINS,
  attributes.PLACEHOLDER,
  attributes.READONLY,
  attributes.SIZE,
  attributes.TABBABLE,
  attributes.VALIDATE,
  attributes.VALIDATION_EVENTS,
  attributes.VALUE,
  attributes.ROUND_TO_INTERVAL
];

export const IdsTimePickerMixinAttributes = [
  attributes.END_HOUR,
  attributes.FORMAT,
  attributes.HOURS,
  attributes.MINUTES,
  attributes.MINUTE_INTERVAL,
  attributes.PERIOD,
  attributes.SECONDS,
  attributes.SECOND_INTERVAL,
  attributes.START_HOUR,
  attributes.USE_CURRENT_TIME,
];

export const range: any = (start: any, stop: any, step = 1) => (
  start > stop ? [] : [start, ...range(start + Math.abs(step), stop, step)]
);

/**
 * Constructs a time string from time parts based on the specified format
 * @param {TimeParts} timeParts - Object containing hours, minutes, seconds and period
 * @param {string} format - The desired time format (e.g., 'HH:mm', 'hh:mm a')
 * @returns {string} Formatted time string
 */
export const constructTimeString = (timeParts: TimeParts, format: string): string => {
  // Pad numbers with leading zeros
  const pad = (num: number | null): string => (num !== null ? num.toString().padStart(2, '0') : '00');

  // Get format segments
  const formatSegments = format.split(/[\s:]+/);
  const separators = format.match(/[:\s]/g) || [];
  let result = '';
  let separatorIndex = 0;

  // Process each format segment
  formatSegments.forEach((segment, index) => {
    switch (segment.toLowerCase()) {
      case 'hh':
      case 'h': {
        const hours = timeParts.hours;
        // Convert 24h to 12h if needed
        if (hours !== null) {
          let hour12 = hours;

          if (hours > 12) {
            hour12 = hours - 12;
          } else if (hours === 0) {
            hour12 = 12;
          }

          result += segment === 'hh' ? pad(hour12) : hour12;
        }
        break;
      }
      case 'HH':
      case 'H': {
        result += segment === 'HH' ? pad(timeParts.hours) : timeParts.hours;
        break;
      }
      case 'mm':
      case 'm': {
        result += segment === 'mm' ? pad(timeParts.minutes) : timeParts.minutes;
        break;
      }
      case 'ss':
      case 's': {
        result += segment === 'ss' ? pad(timeParts.seconds) : timeParts.seconds;
        break;
      }
      case 'a': {
        result += timeParts.period || 'AM';
        break;
      }
      default: result += '';
    }

    // Add separator if not last segment
    if (index < formatSegments.length - 1 && separatorIndex < separators.length) {
      result += separators[separatorIndex];
      separatorIndex++;
    }
  });

  return result;
};

/**
 * Parses a time string according to a specified format and returns time components
 * @param {string|null} timeOnField - The time string to parse (e.g., "10:30 AM")
 * @param {string} format - The format string specifying the time format (e.g., "HH:mm a")
 * @returns {TimeParts} An object containing parsed time components:
 */
export const parseTimeFromFormat = (timeOnField: string | null, format: string): TimeParts => {
  const parts = {
    hours: 0,
    minutes: 0,
    seconds: 0,
    period: ''
  };

  // Return empty parts if no time value
  if (!timeOnField || !format) {
    return parts;
  }

  // Split time and format into segments
  const timeSegments = timeOnField.split(/[\s:]+/);
  const formatSegments = format.split(/[\s:]+/);

  // Map format characters to their positions
  const formatMap = formatSegments.reduce((acc: { [key: string]: number }, segment, index) => {
    acc[segment.toLowerCase()] = index;
    return acc;
  }, {});

  // Parse hours
  if (formatMap.hasOwnProperty('hh') || formatMap.hasOwnProperty('h')) {
    parts.hours = parseInt(timeSegments[formatMap.hh || formatMap.h], 10);
  }
  if (formatMap.hasOwnProperty('HH') || formatMap.hasOwnProperty('H')) {
    parts.hours = parseInt(timeSegments[formatMap.HH || formatMap.H], 10);
  }

  // Parse minutes
  if (formatMap.hasOwnProperty('mm') || formatMap.hasOwnProperty('m')) {
    parts.minutes = parseInt(timeSegments[formatMap.mm || formatMap.m], 10);
  }

  // Parse seconds
  if (formatMap.hasOwnProperty('ss') || formatMap.hasOwnProperty('s')) {
    parts.seconds = parseInt(timeSegments[formatMap.ss || formatMap.s], 10);
  }

  // Parse period (AM/PM)
  if (formatMap.hasOwnProperty('a')) {
    let periodValue = timeSegments[formatMap.a]?.toUpperCase();

    if (!(periodValue === 'AM' || periodValue === 'PM')) {
    // If not found at mapped index, search within timeSegments
      const foundPeriod = timeSegments.find((segment) => ['AM', 'PM'].includes(segment.toUpperCase()));
      periodValue = foundPeriod?.toUpperCase() || '';
    }

    parts.period = periodValue;
  }

  return parts;
};
